import { useCallback } from "react";
import { CombinedGraphQLErrors } from "@apollo/client";
import { useRouter } from "next/navigation";

import getClient from "@/components/apollo-provider";
import ConfirmSetAvatarModal from "@/components/modal/components/confirm-set-avatar";
import NotSignedInModal from "@/components/modal/components/not-signed-in";
import SaveFailedModal from "@/components/modal/components/save-failed";
import SavingModal from "@/components/modal/components/saving";
import UserBanned from "@/components/modal/components/user-banned";
import UserUnverified from "@/components/modal/components/user-unverified";
import { useCurrentUser } from "@/components/user-context-provider/user-context-provider";
import { AnimationType } from "@/gql/generated/graphql";
import { ANIMATION } from "@/gql/queries/animation";
import { MUTATE_ANIMATION } from "@/gql/queries/mutate-animation";
import { serialiseSequence } from "@/lib/editor-helpers";
import { cleanupService, save as saveService } from "@/lib/services/animation";
import * as animationActions from "@/lib/state/actions/animation-actions";
import { store, useAppDispatch } from "@/lib/state/store";

import { useModal } from "../use-modal";

const useSave = () => {
  const { animation } = store.getState();
  const user = useCurrentUser();
  const { openModal, closeModal } = useModal();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const saveAnimation = useCallback(async () => {
    const client = await getClient();
    let { id, url } = animation;
    let version = 1;

    if (!user?.id) {
      openModal(() => <NotSignedInModal />);
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const onCancel = () => {
      abortController.abort();
    };

    openModal(() => (
      <SavingModal
        progress={0}
        message="Saving frames..."
        onCancel={onCancel}
      />
    ));

    try {
      const { flattenedSequence, images } = serialiseSequence(
        animation.sequence,
      );

      if (!animation.id) {
        // New animation - create it first
        const createdAnimation = await client.mutate({
          mutation: MUTATE_ANIMATION,
          variables: {
            title: animation.title,
            sequence: flattenedSequence,
            public: animation.public,
            palette: animation.palette,
            width: animation.width,
            height: animation.height,
            layers: animation.layers,
            schemaVersion: 3,
            folderId: animation.folderId,
            pixelMode: animation.pixelMode,
            type: animation.type,
          },
        });

        if (!createdAnimation.data)
          throw new Error("Failed to create animation");

        ({ id, url } = createdAnimation.data.animation);
        dispatch(animationActions.setUrl(url));
        dispatch(animationActions.setId(id));
        router.replace(`/editor/${url}`);
      } else {
        if (!url) throw new Error("No animation URL to load the animation");
        const animationData = await client.query({
          query: ANIMATION,
          variables: {
            slug: url,
          },
        });
        version = (animationData?.data?.animation?.version ?? 0) + 1;
      }

      const onFrameCompleted = () => {
        return (progress?: number) => {
          openModal(() => (
            <SavingModal
              progress={progress ?? 0}
              message="Saving frames..."
              onCancel={onCancel}
              version={version}
            />
          ));
        };
      };

      await saveService(url, images, version, onFrameCompleted(), signal);

      openModal(() => (
        <SavingModal
          progress={100}
          message="Finalising..."
          onCancel={onCancel}
          version={version}
        />
      ));

      // Commit the new version
      await client.mutate({
        mutation: MUTATE_ANIMATION,
        variables: {
          id,
          title: animation.title,
          sequence: flattenedSequence,
          public: animation.public,
          palette: animation.palette,
          width: animation.width,
          height: animation.height,
          layers: animation.layers,
          version,
          schemaVersion: 3,
          requestEncode: true,
          folderId: animation.folderId,
          pixelMode: animation.pixelMode,
          type: animation.type,
        },
      });

      await cleanupService({
        slug: url,
        currentVersion: version,
        abortSignal: signal,
      });

      closeModal();
      dispatch(animationActions.setSaved(true));

      if (
        animation.type === AnimationType.Avatar &&
        user.avatarUrl !== animation.url &&
        animation.url !== null
      ) {
        openModal(() => (
          <ConfirmSetAvatarModal animationUrl={animation.url ?? ""} />
        ));
      }
    } catch (e) {
      if (CombinedGraphQLErrors.is(e)) {
        if (e.errors.find((c) => c?.extensions?.code === "USER_BANNED")) {
          openModal(() => <UserBanned />);
          return;
        }
        if (
          e.errors.find((c) => c?.extensions?.code === "USER_UNVERIFIED")
        ) {
          openModal(() => <UserUnverified />);
          return;
        }
      }

      // if (e.status === 400 && e.response.message === "Animation does not exist") {
      //   // Animation was deleted, reset and try again
      //   dispatch(animationActions.setUrl(null));
      //   dispatch(animationActions.setId(null));
      //   return dispatch(save({ history }));
      // }
      console.error(e);
      openModal(() => <SaveFailedModal />);
      return;
    }
  }, [
    animation,
    closeModal,
    dispatch,
    openModal,
    router,
    user?.avatarUrl,
    user?.id,
  ]);

  return { saveAnimation };
};

export { useSave };
